<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div class="flex items-center justify-center mb-4">
          <img src="/logo.png" alt="Happy Alien" class="w-20 h-20 object-contain" />
        </div>
        <h2 class="text-3xl font-bold text-gray-900">Set New Password</h2>
        <p class="mt-2 text-gray-600">
          Enter your new password below.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="isInitializing" class="card">
        <div class="text-center space-y-4">
          <div class="w-8 h-8 border-2 border-t-transparent border-primary rounded-full animate-spin mx-auto"></div>
          <p class="text-gray-600">Verifying reset link...</p>
        </div>
      </div>

      <!-- No Token State -->
      <div v-else-if="!hasValidSession && !isSuccess" class="card">
        <div class="text-center space-y-4">
          <div class="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <PhWarning class="w-8 h-8 text-red-600" weight="fill" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Invalid or expired link</h3>
          <p class="text-gray-600">
            This password reset link is invalid or has expired.
          </p>
          <p v-if="debugInfo" class="text-xs text-gray-400 mt-2">{{ debugInfo }}</p>
          <router-link
            to="/forgot-password"
            class="inline-block btn-primary"
          >
            Request a new link
          </router-link>
        </div>
      </div>

      <!-- Success State -->
      <div v-else-if="isSuccess" class="card">
        <div class="text-center space-y-4">
          <div class="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <PhCheckCircle class="w-8 h-8 text-green-600" weight="fill" />
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Password updated!</h3>
          <p class="text-gray-600">
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <router-link
            to="/login"
            class="inline-block btn-primary"
          >
            Sign in
          </router-link>
        </div>
      </div>

      <!-- Form State -->
      <div v-else class="card">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Error Message -->
          <div v-if="error" class="rounded-xl p-4 border bg-red-50 border-red-200">
            <div class="flex">
              <PhWarning class="w-5 h-5 flex-shrink-0 text-red-400" weight="fill" />
              <div class="ml-3">
                <p class="text-sm text-red-800">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- New Password -->
          <div>
            <label for="password" class="block text-sm font-medium mb-1 text-gray-700">
              New password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              minlength="8"
              class="input"
              placeholder="Enter new password"
              :disabled="isLoading"
            />
            <p class="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium mb-1 text-gray-700">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              required
              minlength="8"
              class="input"
              placeholder="Confirm new password"
              :disabled="isLoading"
            />
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full btn-primary flex items-center justify-center"
          >
            <div
              v-if="isLoading"
              class="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
            ></div>
            {{ isLoading ? "Updating..." : "Update password" }}
          </button>

          <!-- Back to login -->
          <div class="text-center">
            <router-link
              to="/login"
              class="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80"
            >
              <PhArrowLeft class="w-4 h-4 mr-1" />
              Back to sign in
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { createClient } from "@supabase/supabase-js";
import { PhWarning, PhCheckCircle, PhArrowLeft } from "@phosphor-icons/vue";

const route = useRoute();

const password = ref("");
const confirmPassword = ref("");
const isLoading = ref(false);
const isInitializing = ref(true);
const error = ref("");
const isSuccess = ref(false);
const hasValidSession = ref(false);
const debugInfo = ref("");

// Create Supabase client
const supabase = createClient(
  (import.meta as any).env.VITE_SUPABASE_URL,
  (import.meta as any).env.VITE_SUPABASE_ANON_KEY
);

onMounted(async () => {
  console.log("[ResetPassword] Initializing...");
  console.log("[ResetPassword] Full URL:", window.location.href);
  console.log("[ResetPassword] Hash:", window.location.hash);
  console.log("[ResetPassword] Query:", route.query);

  try {
    // Check for hash fragment parameters (Supabase's default method)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const type = hashParams.get("type");

    console.log("[ResetPassword] Hash params:", {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      type
    });

    // Also check for code in query params (PKCE flow)
    const code = route.query.code as string;
    console.log("[ResetPassword] Code param:", code);

    if (accessToken && type === "recovery") {
      // Set the session from hash fragments
      console.log("[ResetPassword] Setting session from hash fragments...");
      const { data, error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || "",
      });

      if (sessionError) {
        console.error("[ResetPassword] Session error:", sessionError);
        debugInfo.value = `Session error: ${sessionError.message}`;
        hasValidSession.value = false;
      } else if (data.session) {
        console.log("[ResetPassword] Session set successfully");
        hasValidSession.value = true;
        // Clear the hash from URL for cleaner UX
        window.history.replaceState(null, "", window.location.pathname);
      }
    } else if (code) {
      // PKCE flow - exchange code for session
      console.log("[ResetPassword] Exchanging code for session...");
      const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

      if (exchangeError) {
        console.error("[ResetPassword] Code exchange error:", exchangeError);
        debugInfo.value = `Code exchange error: ${exchangeError.message}`;
        hasValidSession.value = false;
      } else if (data.session) {
        console.log("[ResetPassword] Code exchanged successfully");
        hasValidSession.value = true;
        // Clear the code from URL
        window.history.replaceState(null, "", window.location.pathname);
      }
    } else {
      // Try to get existing session (in case Supabase already handled it)
      console.log("[ResetPassword] Checking for existing session...");
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        console.log("[ResetPassword] Found existing session");
        // Check if this looks like a recovery session
        // We'll allow password update if there's any valid session here
        hasValidSession.value = true;
      } else {
        console.log("[ResetPassword] No valid session found");
        debugInfo.value = "No access_token, code, or existing session found";
        hasValidSession.value = false;
      }
    }
  } catch (err) {
    console.error("[ResetPassword] Initialization error:", err);
    debugInfo.value = `Init error: ${err instanceof Error ? err.message : "Unknown"}`;
    hasValidSession.value = false;
  } finally {
    isInitializing.value = false;
  }
});

async function handleSubmit() {
  // Validation
  if (!password.value || !confirmPassword.value) {
    error.value = "Please fill in all fields";
    return;
  }

  if (password.value.length < 8) {
    error.value = "Password must be at least 8 characters";
    return;
  }

  if (password.value !== confirmPassword.value) {
    error.value = "Passwords do not match";
    return;
  }

  isLoading.value = true;
  error.value = "";

  try {
    console.log("[ResetPassword] Updating password...");

    // Use Supabase client directly to update password
    const { data, error: updateError } = await supabase.auth.updateUser({
      password: password.value,
    });

    if (updateError) {
      console.error("[ResetPassword] Update error:", updateError);
      error.value = updateError.message || "Failed to update password";
      return;
    }

    console.log("[ResetPassword] Password updated successfully");

    // Sign out to clear the recovery session
    await supabase.auth.signOut();

    isSuccess.value = true;
  } catch (err) {
    console.error("[ResetPassword] Error:", err);
    error.value = "An unexpected error occurred. Please try again.";
  } finally {
    isLoading.value = false;
  }
}
</script>
